import React, { useMemo } from 'react';
import { MenuItem } from '@mui/material';
import {
  ListPageFilterContainer,
  ListPageFilterCollapse,
  ListPageFilterTextField,
  ListPagePrimaryFilterRow,
  ListPageQuadSecondaryFilterRow,
  ListPageActiveFilters,
  type ListPageActiveFilterChip,
} from '../../common/list-page';
import type { SellerManagementFiltersState } from './types';

export interface SellerManagementFiltersProps {
  filters: SellerManagementFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<SellerManagementFiltersState>>;
  appliedFilters: SellerManagementFiltersState;
  onClearAppliedField: (field: keyof SellerManagementFiltersState) => void;
  showMoreFilters: boolean;
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
  locations: Array<{ id: number; name: string }>;
  isLoading?: boolean;
}

const SellerManagementFilters: React.FC<SellerManagementFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
  locations,
  isLoading = false,
}) => {
  const handleKeyDownSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  const moreFiltersActiveCount =
    (filters.locationId !== '' ? 1 : 0) +
    (filters.saleDateFrom.trim() !== '' ? 1 : 0) +
    (filters.saleDateTo.trim() !== '' ? 1 : 0);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.searchTerm) {
      chips.push({
        key: 'searchTerm',
        label: `Búsqueda: ${applied.searchTerm}`,
        onRemove: () => onClearAppliedField('searchTerm'),
      });
    }
    if (applied.locationId) {
      const locName =
        locations.find((l) => l.id.toString() === applied.locationId)?.name || applied.locationId;
      chips.push({
        key: 'locationId',
        label: `Sucursal: ${locName}`,
        onRemove: () => onClearAppliedField('locationId'),
      });
    }
    if (applied.saleDateFrom) {
      chips.push({
        key: 'saleDateFrom',
        label: `Ventas desde: ${applied.saleDateFrom}`,
        onRemove: () => onClearAppliedField('saleDateFrom'),
      });
    }
    if (applied.saleDateTo) {
      chips.push({
        key: 'saleDateTo',
        label: `Ventas hasta: ${applied.saleDateTo}`,
        onRemove: () => onClearAppliedField('saleDateTo'),
      });
    }

    return chips;
  }, [appliedFilters, locations, onClearAppliedField]);

  return (
    <ListPageFilterContainer>
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={onToggleMoreFilters}
        onReset={onReset}
        onSearch={onSearch}
        moreFiltersActiveCount={moreFiltersActiveCount}
        searchFieldProps={{
          label: 'Buscar vendedor',
          name: 'searchTerm',
          placeholder: 'Nombre, correo o teléfono',
          value: filters.searchTerm,
          onChange: (e) => setFilters((prev) => ({ ...prev, searchTerm: e.target.value })),
          onKeyDown: handleKeyDownSearch,
          disabled: isLoading,
        }}
      />

      <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <ListPageQuadSecondaryFilterRow>
          <ListPageFilterTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Sucursal"
            name="locationId"
            value={filters.locationId}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                locationId: e.target.value,
              }))
            }
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todas</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={String(loc.id)}>
                {loc.name}
              </MenuItem>
            ))}
          </ListPageFilterTextField>
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Ventas desde"
            name="saleDateFrom"
            type="date"
            value={filters.saleDateFrom}
            onChange={(e) => setFilters((prev) => ({ ...prev, saleDateFrom: e.target.value }))}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Ventas hasta"
            name="saleDateTo"
            type="date"
            value={filters.saleDateTo}
            onChange={(e) => setFilters((prev) => ({ ...prev, saleDateTo: e.target.value }))}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
        </ListPageQuadSecondaryFilterRow>
      </ListPageFilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </ListPageFilterContainer>
  );
};

export default SellerManagementFilters;
